import { Injectable, inject } from '@angular/core';
import { Api } from '../../contracts/classes/api.class';
import { ApiAuthResult, LoginCredentials, LoginResult } from '../../contracts/models/auth.model';
import { BehaviorSubject, Observable, map, tap } from 'rxjs';
import { User } from '../../contracts/models/user.model';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { Storage } from '../../utils/storage.class';
export interface AuthResponse {
    success: boolean;
    user: User;
    token: string;
}
@Injectable({ providedIn: 'root' })
export class AuthService extends Api {

    private router = inject(Router);
    private storage = inject(Storage);

    private readonly authStorage = environment.localStorageKeys.AUTH;
    private readonly tokenStorage = environment.localStorageKeys.TOKEN;
    private readonly loginUrl: string = `${this.urlv2}/auth/login`;

    private userAuthenticated = new BehaviorSubject<User | undefined>(undefined);
    public user$ = this.userAuthenticated.asObservable();

    private token = new BehaviorSubject<string | undefined>(undefined);
    public token$ = this.token.asObservable();

    public isAgent$ = new BehaviorSubject<boolean>(false);
    public isLoggedIn$: Observable<boolean> = this.token.pipe(map(token => Boolean(token) && !this.checkIfTokenHasExpired(token)));

    get isAgent(): boolean {
        return Boolean(this.isAgent$.value);
    }
    get isAdmin(): boolean {
        return !Boolean(this.isAgent$.value);
    }


    constructor() {
        super();
        this.loadSessionFromLocalStorage();
    }

    get user(): User {
        return this.userAuthenticated.value;
    }

    public updateAuthUser(user: User): void {
        this.storage.setItem(this.authStorage, user);
        this.userAuthenticated.next(user);
    }

    public getTokenValue(): string {
        return this.token.value;
    }

    public updateToken(token: string | null): void {
        this.storage.setItem(this.tokenStorage, token);
        this.token.next(token)
    }


    /**
     * @version API.v2
     * @param credentials 
     * @returns 
     */
    public login(credentials: LoginCredentials): Observable<ApiAuthResult> {
        return this.http.post<ApiAuthResult>(this.loginUrl, credentials)
            .pipe(
                tap(result => {
                    try {
                        this.saveItemsInLocalStorage(result['data']);
                        this.saveItemsInObservables(result['data']);
                        //this.redirectToDashboard();
                    } catch (err) {
                        console.error('Error dentro del tap():', err);
                    }
                })
                // 
            );
    }

    public logout(): void {
        // this.http.post(`${this.urlv2}/auth/logout`, {}).subscribe({
        //     next: () => {
                this.router.navigate(['/authentication/login']);
                localStorage.clear();
                this.token.next(undefined);
                this.userAuthenticated.next(undefined);
        //     },
        //     error: (err) => {
        //         console.error('Error al cerrar sesión:', err);
        //         localStorage.clear();
        //         this.router.navigate(['/authentication/login']);

        //     }
        // })

    }

    public savePermisosRolesInLocalStorage(response: any): void {
        const userStr = this.storage.getItem(this.authStorage);
        // this.token.next(response.token);
        if (!userStr) {
            console.warn('No hay usuario en el localStorage');
            return;
        }
        try {
            const user = userStr; // ✅ Parsear aquí correctamente
            // Asegurar que permissions sea un array (por si vino como objeto)
            if (!Array.isArray(response.user.permissions)) {
                response.user.permissions = Object.values(response.user.permissions);
            }
            if (!Array.isArray(response.user.roles)) {
                response.user.roles = Object.values(response.user.roles);
            }

            // Asignar nuevos valores
            user.roles = response.user.roles;
            user.permissions = response.user.permissions;

            //console.log("este es el ddel tenant", user);
            // Asignar los permisos a userAuthenticated si existe
            this.userAuthenticated.next(user);

            const us = user.roles.findIndex(rl => rl.id == 1 || rl.id == 2) == -1
            this.isAgent$.next(us)
            // Guardar el objeto actualizado en el localStorage
            this.storage.setItem(this.authStorage, user); // ← guardar como string
        } catch (error) {
            console.error('Error al parsear el usuario desde localStorage:', error);
        }
    }


    private saveItemsInLocalStorage(data: LoginResult): void {
        this.storage.setItem(this.authStorage, data.user);
        this.storage.setItem(this.tokenStorage, data.token);
    }

    //preguntar si va ser necesario cambiar el login
    private saveItemsInObservables(data: LoginResult): void {
        this.userAuthenticated.next(data.user);
        this.token.next(data.token);
        const us = data.user.roles.findIndex(rl => rl.id == 1 || rl.id == 2) == -1
        this.isAgent$.next(us)
    }

    redirectToDashboard(): void {
        this.router.navigateByUrl('/home');
    }

    private loadSessionFromLocalStorage(): void {
        const user = this.storage.getItem<User>(this.authStorage);
        const token = this.storage.getItem<string>(this.tokenStorage);
        user && token && this.saveItemsInObservables({ user, token });
    }

    /**
     * @param token
     * @returns (true) Si el token ha caducado
     * @returns (false) Si el token sigue vigente
     */
    private checkIfTokenHasExpired(token: string): boolean {
        // Si no hay token, consideramos que está caducado
        if (!token || token == null) { return true; }
        // Dividiendo las partes del token
        const tokenParts = token.split('.');
        // El token JWT debe tener tres partes
        if (tokenParts.length !== 3) { return true; }
        // Decodificar el payload en Base64
        const payload = JSON.parse(atob(tokenParts[1]));
        // Si no hay campo 'exp' en el payload, consideramos que está caducado
        if (!payload.exp || typeof payload.exp !== 'number') { return true; }
        // Convertir a milisegundos
        const expirationTimestamp = payload.exp * 1000;
        // Recuperar la fecha actual
        const currentTimestamp = Date.now();

        return Boolean(currentTimestamp >= expirationTimestamp)
    }

    /**
     * Verifica si el usuario autenticado tiene los permisos ingresados
     * Los permisos son reconocidos por su slug
     * @param operator 
     * @param slugs 
     * @returns 
     */
    public hasPermissions(operator: 'OR' | 'AND', ...slugs: string[]): boolean {
        const _includes: boolean[] = [];

        const _function = {
            'OR': (data: boolean[]) => data.reduce((a, b) => a || b, false),
            'AND': (data: boolean[]) => data.reduce((a, b) => a && b, true)
        }

        for (let i = 0; i < slugs.length; i++) {
            _includes.push(this.isPermit(slugs[i]))
        }

        return _function[operator](_includes)
    }

    /**
     * Verifica si el usuario autenticado tiene el permiso ingresado
     * El permiso son reconocidos por su slug
     * @param permission 
     * @returns 
     */
    public isPermit(permission: string): boolean {
        const permissions = this.userAuthenticated.value.permissions.map(p => p.slug);
        return permissions.includes(permission);
    }

    public isPermitMenu() {
        const permissions = this.userAuthenticated.value.permissions.map(p => p.slug);
        return permissions
    }


    goHome() {
        this.router.navigate(['home']);
    }

    loginWithSocial(provider) {
        const width = 600;
        const height = 700;
        const left = (window.innerWidth - width) / 2;
        const top = (window.innerHeight - height) / 2;

        const popup = window.open(
            `${this.url}/api/auth/${provider}/redirect`,
            'Facebook Login',
            `width=${width},height=${height},top=${top},left=${left}`
        );

        // Escuchar mensajes del popup
        const messageListener = (event: MessageEvent) => {
            // if (event.origin !== environment.app_urls.source) return;
            // console.log('Mensaje recibido:', event.data);

            // if (event.data.type === 'FACEBOOK_AUTH_SUCCESS') {
            // console.log('Datos recibidos del popup:', event.data);

            const { token, user } = event.data;

            // Guardar token y usuario
            this.storage.setItem(this.authStorage, user);
            this.storage.setItem(this.tokenStorage, token);

            // Actualizar observables
            this.userAuthenticated.next(user);
            this.token.next(token);

            // Determinar si es agente
            const isAgent = user.roles.findIndex((rl: any) => rl.id == 1 || rl.id == 2) == -1;
            this.isAgent$.next(isAgent);

            // Redirigir
            this.redirectToDashboard();

            // Remover listener
            window.removeEventListener('message', messageListener);
            // }
        };

        window.addEventListener('message', messageListener);

        // Verificar si el popup se cerró manualmente
        const checkPopup = setInterval(() => {
            if (popup && popup.closed) {
                clearInterval(checkPopup);
                window.removeEventListener('message', messageListener);

            }
        }, 1000);
    }

    loginWithProvider(provider: 'google' | 'linkedin'): Promise<{ user: any, token?: string, is_new: boolean }> {
        return new Promise((resolve, reject) => {
            const authWindow = window.open(
                `${this.url}/auth/${provider}/redirect`,
                '_blank',
                'width=600,height=700'
            );

            const listener = (event: MessageEvent) => {
                if (event.data?.status === 'success') {
                    const { user, token, is_new } = event.data;

                    if (is_new) {
                        // Caso nuevo usuario:
                        // console.log("ingreso a nuevo usuario")
                        window.removeEventListener('message', listener);
                        authWindow?.close();
                        resolve({ user, is_new, token: null });
                    } else {
                        // Caso usuario existente:
                        // console.log("Usuario exitente")
                        this.saveItemsInLocalStorage(event.data);
                        this.saveItemsInObservables(event.data);

                        window.removeEventListener('message', listener);
                        authWindow?.close();
                        resolve({ user, token, is_new });
                    }
                } else if (event.data?.status === 'error') {
                    window.removeEventListener('message', listener);
                    authWindow?.close();
                    reject(event.data);
                }
            };

            window.addEventListener('message', listener);
        });
    }

}
